/**
 * Face filter drawing utilities for canvas-based magnifying lens effects.
 * Extracted from FaceFilter.jsx for testability and reuse.
 */

/**
 * Draw a magnifying lens at (cx, cy) with radius r and zoom factor.
 * Samples the video at a smaller region and draws it scaled into the lens.
 */
export function drawZoomCircle({ ctx, video, cx, cy, radius, scale }) {
  const srcR = radius / scale;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.clip();
  ctx.drawImage(
    video,
    cx - srcR, cy - srcR, srcR * 2, srcR * 2,
    cx - radius, cy - radius, radius * 2, radius * 2
  );
  ctx.restore();

  // Subtle outer ring
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(255,255,255,0.18)';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

export function drawZoomEllipse({ ctx, video, cx, cy, rx, ry, scale }) {
  const srx = rx / scale;
  const sry = ry / scale;
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.clip();
  ctx.drawImage(
    video,
    cx - srx, cy - sry, srx * 2, sry * 2,
    cx - rx, cy - ry, rx * 2, ry * 2
  );
  ctx.restore();
}

/**
 * Funhouse wavy effect: redraw horizontal strips with horizontal offset.
 */
export function drawWavy({ ctx, video, width, height, phase }) {
  const strips = 30;
  const stripH = height / strips;
  for (let i = 0; i < strips; i++) {
    const offset = Math.sin(phase + i * 0.4) * 12;
    ctx.drawImage(
      video,
      0, i * stripH, width, stripH,
      offset, i * stripH, width, stripH
    );
  }
}

/**
 * Compute face landmark approximations (centered face assumption).
 */
export function computeLandmarks(w, h) {
  const minDim = Math.min(w, h);
  return {
    face: { cx: w * 0.5, cy: h * 0.45, r: minDim * 0.28 },
    nose: { cx: w * 0.5, cy: h * 0.52, r: minDim * 0.07 },
    lips: { cx: w * 0.5, cy: h * 0.66, rx: minDim * 0.09, ry: minDim * 0.045 },
  };
}

/**
 * Apply a single effect to the canvas context.
 */
export function applyFaceEffect({ ctx, video, effect, width, height, phase }) {
  const lm = computeLandmarks(width, height);

  switch (effect) {
    case 'big_nose':
      drawZoomCircle({ ctx, video, cx: lm.nose.cx, cy: lm.nose.cy, radius: lm.nose.r, scale: 1.9 });
      return;
    case 'big_lips':
      drawZoomEllipse({ ctx, video, cx: lm.lips.cx, cy: lm.lips.cy, rx: lm.lips.rx, ry: lm.lips.ry, scale: 2.0 });
      return;
    case 'big_head':
      drawZoomCircle({ ctx, video, cx: lm.face.cx, cy: lm.face.cy, radius: lm.face.r, scale: 1.35 });
      return;
    case 'tiny_head':
      drawZoomCircle({ ctx, video, cx: lm.face.cx, cy: lm.face.cy, radius: lm.face.r, scale: 0.7 });
      return;
    case 'wavy':
      drawWavy({ ctx, video, width, height, phase });
      return;
    default:
      return;
  }
}
